// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 20-agent-plugins.md

import * as React from 'react';

import type { OrgRole, OrgSettings } from '@greenlight/shared';

/** Settings → Source control, scrolled to the marketplace create form. */
export const MARKETPLACE_CREATE_PATH = '/settings?tab=scm&focus=marketplace';

export const AGENT_SETUP_PATH = '/settings/agent-setup';

/**
 * Which plugin-setup state a viewer sees (docs/28 §Agent setup): only an org
 * admin can create a marketplace, so an `admin` without one waits like a
 * builder. An unresolved role gets the builder states.
 */
export type PluginSetupState =
  | 'admin-rollout'
  | 'admin-ready'
  | 'builder-waiting'
  | 'builder-ready';

export function pluginSetupState(
  role: OrgRole | undefined,
  hasMarketplace: boolean,
): PluginSetupState {
  const isIT = role === 'org_admin' || role === 'admin';
  if (hasMarketplace) return isIT ? 'admin-ready' : 'builder-ready';
  return role === 'org_admin' ? 'admin-rollout' : 'builder-waiting';
}

type OrgAdmin = NonNullable<OrgSettings['agent_setup']['org_admins']>[number];

function joinWithOr(parts: React.ReactNode[]): React.ReactNode[] {
  return parts.flatMap((part, i) => {
    if (i === 0) return [part];
    const separator = i === parts.length - 1 ? (parts.length > 2 ? ', or ' : ' or ') : ', ';
    return [separator, part];
  });
}

/** "Ask Ada Lovelace or grace@example.com to set it up." with mail links. */
export function OrgAdminContacts({
  admins,
}: {
  admins: readonly OrgAdmin[] | undefined;
}): React.JSX.Element {
  if (!admins || admins.length === 0) {
    return <>Ask a Greenlight org admin to set it up.</>;
  }
  const links = admins.map((admin) => (
    <a
      key={admin.email}
      href={`mailto:${admin.email}`}
      className="font-medium text-foreground underline underline-offset-2"
    >
      {admin.display_name ?? admin.email}
    </a>
  ));
  return (
    <>
      Ask {joinWithOr(links)}, {admins.length === 1 ? 'your org admin' : 'your org admins'}, to set
      it up.
    </>
  );
}
